import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { SelectOption, DataSourceConfig } from '../../models/form-field.model';
import { ApiError, ApiResponse } from '../../models/api.model';
import { Store } from '@ngrx/store';
import { AuthUser } from '../../models';
import * as AuthSelectors from '@app/store/auth/auth.selectors';
import { I18nService } from '../common/i18n.service';
import { EnvService } from '../common/env.service';

@Injectable({ providedIn: 'root' })
export class OptionsLoaderService {
  private readonly httpClient = inject(HttpClient);
  protected readonly store = inject(Store);
  protected readonly i18n = inject(I18nService);
  protected readonly envService = inject(EnvService);
  protected readonly businessEndpoint = this.envService.get<string>('businessEndpoint');

  user$ = this.store.select(AuthSelectors.selectUser);
  protected currentUser: AuthUser | null = null;

  constructor() {
    this.user$.subscribe((user) => (this.currentUser = user));
  }

  loadOptions(
    config: DataSourceConfig = { bizType: '', labelField: '', valueField: '' },
    onSuccess?: (data: SelectOption[]) => void,
    onError?: (error: ApiError) => void,
  ): Observable<SelectOption[]> {
    const method = config.method || 'POST';
    const url = this.buildFullUrl(config.url);

    // Build request based on method
    if (method === 'POST') {
      return this.loadOptionsViaPost(url, config, onSuccess, onError);
    } else if (method === 'GET') {
      return this.loadOptionsViaGet(url, config, onSuccess, onError);
    }

    return of([]);
  }

  /**
   * Build full URL from config
   */
  private buildFullUrl(configUrl?: string): string {
    if (!configUrl || configUrl.trim() === '') {
      return `${this.envService.getApiUrl()}${this.businessEndpoint}`;
    }

    const trimmedUrl = configUrl.trim();
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
      return trimmedUrl;
    }

    if (!trimmedUrl.startsWith('/') && trimmedUrl.includes('.')) {
      return `https://${trimmedUrl}`;
    }

    return `${this.envService.getApiUrl()}${trimmedUrl}`;
  }

  private loadOptionsViaPost(
    url: string,
    config: DataSourceConfig,
    onSuccess?: (data: SelectOption[]) => void,
    onError?: (error: ApiError) => void,
  ): Observable<SelectOption[]> {
    const request: any = {
      JsonHeader: {
        lang: this.i18n.getCurrentLang(),
        user: this.currentUser?.LoginName ?? '',
        channel: 'BACK',
        branchCode: this.currentUser?.branchCode ?? '',
        branchId: this.currentUser?.branchId ?? 0,
      },
      ...config.body,
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });

    return this.httpClient.post<ApiResponse<unknown[]>>(url, request, { headers }).pipe(
      map((response: ApiResponse<unknown[]>) => {
        if (response.Rc !== 0) {
          throw new Error(response.RsMsg || 'Failed to load options');
        }

        const data = this.extractDataFromResponse(response, config.responsePath);
        const options = this.mapToSelectOptions(data, config);

        if (onSuccess) {
          onSuccess(options);
        }

        return options;
      }),
      catchError((error) => {
        console.error('Error loading options:', error);
        if (onError) {
          onError({
            code: error.status || 500,
            message: error.message || 'Failed to load options',
          });
        }
        return of([]);
      }),
    );
  }

  private loadOptionsViaGet(
    url: string,
    config: DataSourceConfig,
    onSuccess?: (data: SelectOption[]) => void,
    onError?: (error: ApiError) => void,
  ): Observable<SelectOption[]> {
    // Build query params
    const params = new URLSearchParams();
    if (config.params) {
      Object.entries(config.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }

    const fullUrl = params.toString() ? `${url}?${params.toString()}` : url;
    const headers = new HttpHeaders({
      Accept: 'application/json',
      ...config.headers,
    });

    return this.httpClient.get<ApiResponse<unknown[]>>(fullUrl, { headers }).pipe(
      map((response: ApiResponse<unknown[]>) => {
        if (response.Rc !== 0) {
          throw new Error(response.RsMsg || 'Failed to load options');
        }

        const data = this.extractDataFromResponse(response, config.responsePath);
        const options = this.mapToSelectOptions(data, config);

        if (onSuccess) {
          onSuccess(options);
        }

        return options;
      }),
      catchError((error) => {
        console.error('Error loading options:', error);
        if (onError) {
          onError({
            code: error.status || 500,
            message: error.message || 'Failed to load options',
          });
        }
        return of([]);
      }),
    );
  }

  private extractDataFromResponse(
    response: ApiResponse<unknown[]>,
    responsePath?: string,
  ): unknown[] {
    if (!responsePath) {
      return response.Data || [];
    }

    // Navigate through response path (e.g., "data.items")
    const paths = responsePath.split('.');
    let data: any = response;

    for (const path of paths) {
      if (data && typeof data === 'object' && path in data) {
        data = data[path];
      } else {
        return [];
      }
    }

    return Array.isArray(data) ? data : [];
  }

  private mapToSelectOptions(data: unknown[], config: DataSourceConfig): SelectOption[] {
    if (!Array.isArray(data)) return [];

    return data
      .filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
      .map((item) => ({
        label: String(item[config.labelField || 'label'] || item['name'] || item),
        value: item[config.valueField || 'value'] || item['id'] || item,
      }));
  }
}
